import { useMemo } from 'react';
import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { Deactivation2 } from '@elfsight/widget-deactivation';
import { FreeLink, pickFreeLinkSettings } from '@elfsight/widget-free-link';
import { Loader } from '@elfsight/widget-ui';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { DEFAULT_API_UNITS } from '../constants';
import { meta } from '../meta';
import type { ChildrenData } from './components/api/data';
import { Data } from './components/api/data';
import type {
  ForecastDailyListItem,
  ForecastListItem
} from './components/api/request';
import { iconStyles } from './components/common/icon-styles';
import { Daily } from './components/daily/daily';
import { Detail } from './components/detail';
import { Forecast } from './components/forecast/forecast';
import { elements, getClassName, getOpenWeatherLanguage } from './helpers';

const isNumericCoordinate = (value: string) =>
  Boolean(value) &&
  /^-?\d+(\.\d+)?$/.test(value) &&
  Number.isFinite(Number(value));

export function Widget({
  customCSSPrefixClassName
}: {
  customCSSPrefixClassName?: string;
}) {
  const { t } = useLingui();
  const settings = useSettings();
  const freeLinkSettings = pickFreeLinkSettings(settings);
  const {
    typeLocation,
    location,
    language,
    layout,
    currentTemperatureSize,
    currentWeatherSize,
    backgroundColor,
    backgroundType,
    iconsAnimation
  } = settings;

  const { language: locale } = language ?? {};
  const openWeatherLanguage = getOpenWeatherLanguage(locale);
  const locationValue = location.trim();
  const [rawLat, rawLon] = locationValue.split(',').map((part) => part.trim());

  const hasCoordinates =
    isNumericCoordinate(rawLat) && isNumericCoordinate(rawLon);
  const isUserLocation = typeLocation === 'userLocation';
  const hasGeoSource = isUserLocation || hasCoordinates;

  const coordinatesParams = useMemo(
    () =>
      !isUserLocation && hasCoordinates ? { lat: rawLat, lon: rawLon } : {},
    [isUserLocation, hasCoordinates, rawLat, rawLon]
  );

  const size = (() => {
    if (layout === 'currentTemperature') {
      return currentTemperatureSize;
    }

    if (layout === 'currentWeather') {
      return currentWeatherSize;
    }

    return 0;
  })();

  const sourceName = hasGeoSource ? '' : locationValue;
  const hasRequestSource = hasGeoSource || !!sourceName;

  const forecastParams = useMemo(
    () => ({
      cnt: 8,
      lang: openWeatherLanguage,
      units: DEFAULT_API_UNITS,
      ...coordinatesParams
    }),
    [coordinatesParams, openWeatherLanguage]
  );

  const dailyParams = useMemo(
    () => ({
      cnt: 7,
      lang: openWeatherLanguage,
      units: DEFAULT_API_UNITS,
      ...coordinatesParams
    }),
    [coordinatesParams, openWeatherLanguage]
  );

  const displayFreeLinkTextMode =
    layout === 'currentTemperature' || layout === 'currentWeather'
      ? 'text'
      : 'button';
  const wrongLocationErrorText = t`Wrong location name, please check and try again.`;

  return (
    <Deactivation2 {...settings} appAlias={meta.alias}>
      <Component
        _layout={layout}
        _iconsAnimation={iconsAnimation}
        className={customCSSPrefixClassName}
      >
        <Info _layout={layout} _size={size}>
          <Show when={hasRequestSource}>
            <Data<ForecastListItem>
              sourceName={sourceName}
              sourceType=""
              endpoint="forecast"
              useUserLocation={isUserLocation}
              params={forecastParams}
            >
              {({ data, loading, error }: ChildrenData<ForecastListItem>) => {
                if (loading) {
                  return (
                    <SpinnerContainer>
                      <Loader
                        size={30}
                        className={getClassName('spinner', 'component')}
                      />
                    </SpinnerContainer>
                  );
                }

                if (data) {
                  const {
                    weather: [currentWeather]
                  } = data.list[0];
                  const { icon } = currentWeather;

                  return (
                    <DetailContainer
                      _layout={layout}
                      _size={size}
                      style={{
                        background:
                          backgroundType === 'image'
                            ? `${backgroundColor} url(https://files.elfsight.com/storage/_assets/weather/bg-images/${icon}.jpg)`
                            : backgroundColor
                      }}
                    >
                      <Detail data={data} />

                      <Show when={elements.showForecast(layout)}>
                        <Forecast data={data} />
                      </Show>
                    </DetailContainer>
                  );
                }

                if (error?.code === 400) {
                  return <Error>{wrongLocationErrorText}</Error>;
                }

                return null;
              }}
            </Data>
          </Show>

          <Show when={elements.showDaily(layout) && hasRequestSource}>
            <Data<ForecastDailyListItem>
              sourceName={sourceName}
              sourceType=""
              endpoint="forecast/daily"
              useUserLocation={isUserLocation}
              params={dailyParams}
            >
              {({ data, loading }: ChildrenData<ForecastDailyListItem>) => {
                return data && !loading ? <Daily data={data} /> : null;
              }}
            </Data>
          </Show>
        </Info>

        <FreeLink
          {...freeLinkSettings}
          appAlias={meta.alias}
          appName={meta.name}
          freeLinkAnchor={undefined} // use default template for anchor text
          mode={displayFreeLinkTextMode}
          iconHidden={displayFreeLinkTextMode === 'text'}
          textModeProps={{
            forceTwoLines: true,
            customColor: 'rgba(113, 113, 113, 0.8)', // color for outside view
            fontSize: 9
          }}
          style={{
            link: {
              marginTop: displayFreeLinkTextMode === 'text' ? '8px' : '20px',
              ...(displayFreeLinkTextMode === 'text' && {
                display: 'flex',
                width: 'fit-content'
              }),
              ...(layout === 'currentWeather' && {
                transform: 'none',
                left: 'auto'
              })
            }
          }}
        />
      </Component>
    </Deactivation2>
  );
}

const Component = styled.div.attrs<{
  _layout: string;
  _iconsAnimation: boolean;
}>({
  className: getClassName('weather', 'component')
})`
  ${({ _iconsAnimation }) => (_iconsAnimation ? iconStyles : '')};

  max-width: 300px;
  width: 100%;
  display: inline-block;
  position: relative;

  &:hover .react-horizontal-scrolling-menu--arrow-left,
  &:hover .react-horizontal-scrolling-menu--arrow-right {
    opacity: 1;
  }

  ${({ _layout }) =>
    _layout === 'currentTemperature' || _layout === 'currentWeather'
      ? `
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        max-width: inherit;
        width: auto;
      `
      : ''}
`;

const Info = styled.div.attrs<{
  _layout: string;
  _size: number;
}>({
  className: getClassName('weather', 'info')
})`
  overflow: hidden;
  position: relative;
  border-radius: 10px;

  ${({ _layout, _size }) =>
    _layout === 'currentTemperature' || _layout === 'currentWeather'
      ? `
        display: inline-flex;
        border-radius: ${_size / 3}px;
      `
      : ''}
`;

const DetailContainer = styled.div.attrs<{
  _layout: string;
  _size: number;
}>({
  className: getClassName('weather', 'detail')
})`
  color: #fff;
  padding-bottom: 20px;
  background-size: cover !important;
  background-position: center !important;

  ${({ _layout, _size }) => `
    ${
      _layout === 'currentTemperature'
        ? `
          padding: 0 ${_size / 3}px 0 ${_size / 2}px;
        `
        : ''
    }
    ${
      _layout === 'currentWeather'
        ? `
          padding: ${_size / 3}px ${_size / 2}px;
        `
        : ''
    }
`}
`;

const Error = styled.div.attrs({
  className: getClassName('weather', 'error')
})`
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  text-align: center;
  padding: 20px;
  background-color: #fff;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  line-height: 1.39;
`;

const SpinnerContainer = styled.div.attrs({
  className: getClassName('weather', 'spinner')
})`
  width: 100%;
  height: 100%;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
